/**
 * Mi Diario — patient food log (adherence loop Fase 1).
 *
 * Design principles (docs/ROADMAP_ADHERENCE_LOOP.md):
 * - Minimal friction: photo + 1 tap. Note / hunger optional.
 * - Anti-guilt: the patient sees the dish name + an empathetic AI message,
 *   never calorie counts or red numbers. Warm welcome always, no streaks.
 * - The nutritionist's reaction (Fase 2) is the loop's emotional payoff,
 *   so it renders prominently when present.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Rating,
  Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  AddAPhoto as AddAPhotoIcon,
  AutoAwesome as AutoAwesomeIcon,
  DeleteOutline as DeleteOutlineIcon,
  Favorite as FavoriteIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import foodLogService from '../../services/foodLogService';
import FoodLogPhoto from './FoodLogPhoto';

const MEAL_TYPES = [
  { id: 'desayuno', label: '🌅 Desayuno' },
  { id: 'colacion', label: '🍎 Colación' },
  { id: 'comida', label: '🍲 Comida' },
  { id: 'cena', label: '🌙 Cena' },
  { id: 'snack', label: '🥜 Snack' },
  { id: 'otro', label: '🍽️ Otro' },
];

// Jade de Códice (DESIGN.md). Antes era #006847, uno de los tres verdes
// sueltos que el sistema de diseño manda unificar.
const GREEN = '#1F5C3D';

const formatWhen = (iso) => {
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Hoy · ${time}`;
  if (isYesterday) return `Ayer · ${time}`;
  return `${d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} · ${time}`;
};

const guessMealType = () => {
  const h = new Date().getHours();
  if (h < 11) return 'desayuno';
  if (h < 13) return 'colacion';
  if (h < 17) return 'comida';
  if (h < 20) return 'cena';
  return 'snack';
};

const FoodLogDiary = () => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Composer state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [mealType, setMealType] = useState(guessMealType());
  const [note, setNote] = useState('');
  const [hunger, setHunger] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setError('');
      const data = await foodLogService.getMine();
      setEntries(data);
    } catch (e) {
      setError('No pudimos cargar tu diario. Intenta de nuevo en un momento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll briefly while any entry's AI analysis is pending.
  useEffect(() => {
    if (!entries.some((e) => e.ai_status === 'pending')) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [entries, load]);

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setMealType(guessMealType());
    setComposerOpen(true);
    e.target.value = ''; // allow re-picking the same file
  };

  const resetComposer = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setNote('');
    setHunger(null);
    setComposerOpen(false);
  };

  const submit = async () => {
    if (!photoFile && !note.trim()) return;
    setSubmitting(true);
    try {
      const entry = await foodLogService.createEntry({
        photoFile, mealType, note: note.trim(), hungerLevel: hunger,
      });
      setEntries((prev) => [entry, ...prev]);
      resetComposer();
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        'No pudimos guardar tu registro. Inténtalo de nuevo.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entryId) => {
    try {
      await foodLogService.deleteEntry(entryId);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
    } catch (e) {
      setError('No se pudo borrar el registro.');
    }
  };

  const mealLabel = (id) => MEAL_TYPES.find((m) => m.id === id)?.label || id;

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {/* Warm header — never guilt-trips, even after days away */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={700} sx={{ color: GREEN }}>
          Mi Diario 📸
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Comparte tu comida con una foto. Tu nutriólogo la verá y te acompañará.
        </Typography>
      </Box>

      {/* Big single-purpose action: photo + 1 tap */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={onPickPhoto}
      />
      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          startIcon={<PhotoCameraIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            bgcolor: GREEN, py: 1.8, borderRadius: 3, fontSize: 16, fontWeight: 700,
            '&:hover': { bgcolor: '#005035' },
          }}
        >
          Foto de mi comida
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => { setMealType(guessMealType()); setComposerOpen(true); }}
          sx={{ borderColor: GREEN, color: GREEN, borderRadius: 3, whiteSpace: 'nowrap' }}
        >
          Sin foto
        </Button>
      </Stack>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      )}

      {/* Feed */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: GREEN }} />
        </Box>
      ) : entries.length === 0 ? (
        <Card elevation={0} sx={{ borderRadius: 3, bgcolor: '#f3f7f4', textAlign: 'center', py: 5, px: 3 }}>
          <Typography variant="h6" sx={{ color: GREEN, mb: 1 }}>
            Tu diario está listo 🌿
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registra tu primera comida con una foto. Sin contar calorías,
            sin presión — solo compartir cómo te alimentas.
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {entries.map((entry) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {entry.has_photo && <FoodLogPhoto entryId={entry.id} height={240} sx={{ borderRadius: 0 }} />}
                <CardContent sx={{ pb: '16px !important' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={mealLabel(entry.meal_type)}
                        sx={{ bgcolor: '#e8f3ee', color: GREEN, fontWeight: 600 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatWhen(entry.logged_at)}
                      </Typography>
                    </Stack>
                    <IconButton size="small" onClick={() => remove(entry.id)} aria-label="Borrar registro">
                      <DeleteOutlineIcon fontSize="small" sx={{ color: 'grey.400' }} />
                    </IconButton>
                  </Stack>

                  {/* Dish name from AI (descriptive, not judgmental) */}
                  {entry.ai_analysis?.platillo?.nombre && (
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2c3e50' }}>
                      {entry.ai_analysis.platillo.nombre}
                    </Typography>
                  )}

                  {entry.note && (
                    <Typography variant="body2" sx={{ color: '#444', mt: 0.5 }}>
                      “{entry.note}”
                    </Typography>
                  )}

                  {/* Empathetic AI message — the patient-facing analysis */}
                  {entry.ai_analysis?.mensaje_paciente ? (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f3f7f4', borderRadius: 2, display: 'flex', gap: 1 }}>
                      <AutoAwesomeIcon sx={{ fontSize: 18, color: GREEN, mt: '2px' }} />
                      <Typography variant="body2" sx={{ color: '#33523f' }}>
                        {entry.ai_analysis.mensaje_paciente}
                      </Typography>
                    </Box>
                  ) : entry.ai_status === 'pending' ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      <CircularProgress size={10} sx={{ mr: 0.8, color: GREEN }} />
                      Huitzi está mirando tu platillo…
                    </Typography>
                  ) : null}

                  {/* Nutritionist reaction (Fase 2) — the emotional payoff */}
                  {entry.reaction && (
                    <Box
                      sx={{
                        mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fff7ec',
                        border: '1px solid #f3dfc0', display: 'flex', gap: 1, alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: 22 }}>{entry.reaction}</Typography>
                      <Box>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#a05f17', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FavoriteIcon sx={{ fontSize: 12 }} /> Tu nutriólogo reaccionó
                        </Typography>
                        {entry.reaction_comment && (
                          <Typography variant="body2" sx={{ color: '#6b4a1f' }}>
                            {entry.reaction_comment}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Stack>
      )}

      {/* Composer dialog — everything beyond the photo is optional */}
      {/* A pantalla completa en el teléfono: con el teclado abierto, un
          diálogo flotante deja el campo de notas en una rendija. */}
      <Dialog
        open={composerOpen}
        onClose={() => !submitting && resetComposer()}
        fullWidth
        maxWidth="xs"
        fullScreen={isPhone}
      >
        <DialogTitle sx={{ fontWeight: 700, color: GREEN }}>
          {photoFile ? '¡Se ve bien! 🌿' : 'Registrar comida'}
        </DialogTitle>
        <DialogContent>
          {photoPreview && (
            <Box
              component="img"
              src={photoPreview}
              alt="Tu comida"
              sx={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 2, mb: 2 }}
            />
          )}
          {!photoFile && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddAPhotoIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2, borderColor: GREEN, color: GREEN, borderRadius: 2 }}
            >
              Agregar foto
            </Button>
          )}

          <Typography variant="caption" fontWeight={700} color="text.secondary">
            ¿Qué comida es?
          </Typography>
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.8, mt: 0.5, mb: 2 }}>
            {MEAL_TYPES.map((m) => (
              <Chip
                key={m.id}
                label={m.label}
                size="small"
                onClick={() => setMealType(m.id)}
                sx={{
                  bgcolor: mealType === m.id ? GREEN : '#eef2ef',
                  color: mealType === m.id ? 'white' : '#446',
                  fontWeight: 600,
                  '&:hover': { bgcolor: mealType === m.id ? '#005035' : '#e0e8e2' },
                }}
              />
            ))}
          </Stack>

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder="Nota opcional — ¿cómo te sentiste? ¿algo que contarle a tu nutriólogo?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            sx={{ mb: 2 }}
          />

          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
            ¿Con cuánta hambre llegaste? (opcional)
          </Typography>
          <Rating
            value={hunger}
            onChange={(_, v) => setHunger(v)}
            max={5}
            icon={<span style={{ fontSize: 20 }}>🌶️</span>}
            emptyIcon={<span style={{ fontSize: 20, opacity: 0.3 }}>🌶️</span>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetComposer} disabled={submitting} sx={{ color: 'grey.600' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={submitting || (!photoFile && !note.trim())}
            sx={{ bgcolor: GREEN, borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#005035' } }}
          >
            {submitting ? 'Guardando…' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FoodLogDiary;
